import { SkipToMainLink } from "@/components/layout/SkipToMainLink";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { LogoLockup } from "@/components/ui/LogoLockup";
import { useAnalytics } from "@/features/analytics/context/AnalyticsProvider";
import { ForgotPasswordForm } from "@/features/auth/components/ForgotPasswordForm";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { auth } from "@/features/auth/utils/firebase";
import { claimEvidenceSignup } from "@/features/claim-hunter/utils/signupSource";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import {
  redirectAfterLogin,
  wantsDocumentReload,
} from "@/utils/resolvePostLoginPath";
import { useGxlFirebaseAuthSync } from "@gxl/web-auth";
import { FirebaseError } from "firebase/app";
import {
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  getAdditionalUserInfo,
  signInWithEmailAndPassword,
  signInWithPopup,
} from "firebase/auth";
import { AlertCircle, CheckCircle } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

type AuthMode = "signin" | "signup" | "forgot";

const formatFirebaseAuthError = (err: unknown, fallback: string): string => {
  if (err instanceof FirebaseError) {
    switch (err.code) {
      case "auth/invalid-credential":
      case "auth/wrong-password":
      case "auth/user-not-found":
        return "Invalid email or password";
      case "auth/user-disabled":
        return "This account has been disabled";
      case "auth/too-many-requests":
        return "Too many failed attempts. Please try again later";
      case "auth/email-already-in-use":
        return "An account with this email already exists";
      case "auth/weak-password":
        return "Password is too weak. Use at least 8 characters with upper/lowercase and a number";
      case "auth/operation-not-allowed":
        return "Email/password accounts are not enabled";
      case "auth/invalid-email":
        return "Invalid email address";
      default:
        return err.message || fallback;
    }
  }
  return err instanceof Error ? err.message : fallback;
};

/** Same-origin relative path only; blocks protocol-relative and external URLs. */
export const parseSafeNext = (raw: string | null): string | null => {
  if (!raw) return null;
  try {
    const decoded = decodeURIComponent(raw);
    if (!decoded.startsWith("/") || decoded.startsWith("//")) return null;
    // Allow citation highlight hashes such as #L16 or #L10-L24.
    return decoded;
  } catch {
    return null;
  }
};

const LoginLegalLinks = () => (
  <nav aria-label="Legal" className="mt-5 text-center text-xs text-zinc-400">
    <a
      href="https://gxl.ai/terms-of-service"
      target="_blank"
      rel="noopener noreferrer"
      className="underline underline-offset-2 decoration-zinc-300 hover:text-zinc-700 hover:decoration-zinc-400"
    >
      Terms of Service
      <span className="sr-only"> (opens in new tab)</span>
    </a>
    <span className="mx-2 text-zinc-300" aria-hidden>
      ·
    </span>
    <a
      href="https://gxl.ai/privacy-notice"
      target="_blank"
      rel="noopener noreferrer"
      className="underline underline-offset-2 decoration-zinc-300 hover:text-zinc-700 hover:decoration-zinc-400"
    >
      Privacy Notice
      <span className="sr-only"> (opens in new tab)</span>
    </a>
  </nav>
);

export const LoginPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const nextRaw = searchParams.get("next");
  const safeNext = useMemo(() => parseSafeNext(nextRaw), [nextRaw]);
  const reload = wantsDocumentReload(searchParams.get("reload"));
  const { trackEvent } = useAnalytics();
  const { user } = useAuth();

  const [error, setError] = useState("");
  const [cliSuccess, setCliSuccess] = useState(false);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [authMode, setAuthMode] = useState<AuthMode>(
    searchParams.get("mode") === "signup" ? "signup" : "signin",
  );
  const [submitting, setSubmitting] = useState(false);

  const noop = useCallback(async () => { }, []);

  const { firebaseUser } = useGxlFirebaseAuthSync({
    auth,
    onSignedIn: noop,
    onSignedOut: noop,
  });

  const signedIn = !!firebaseUser;

  // OAuth PKCE params
  const isOAuth = searchParams.get("oauth") === "true";
  const redirectUri = searchParams.get("redirect_uri");
  const codeChallenge = searchParams.get("code_challenge");
  const codeChallengeMethod = searchParams.get("code_challenge_method");
  const oauthState = searchParams.get("state");
  const isOAuthLogin = isOAuth && !!redirectUri && !!codeChallenge;

  // Device code flow
  const isDeviceLogin = searchParams.get("device") === "true";
  const prefillCode = searchParams.get("code") || "";
  const [deviceCode, setDeviceCode] = useState(prefillCode.toUpperCase());
  const [deviceSuccess, setDeviceSuccess] = useState(false);
  const [deviceSubmitting, setDeviceSubmitting] = useState(false);

  const documentTitle = useMemo(() => {
    if (deviceSuccess || cliSuccess) return "Authenticated";
    if (isDeviceLogin && signedIn) return "Enter your code";
    if (authMode === "forgot") return "Reset your password";
    if (authMode === "signup") return "Create account";
    if (isOAuthLogin || isDeviceLogin) return "Sign in";
    return "Sign in";
  }, [
    deviceSuccess,
    cliSuccess,
    isDeviceLogin,
    signedIn,
    authMode,
    isOAuthLogin,
  ]);
  useDocumentTitle(documentTitle);

  const oauthAttempted = useRef(false);
  const deviceAutoSubmitted = useRef(false);
  useEffect(() => {
    if (signedIn && isOAuthLogin && !oauthAttempted.current) {
      oauthAttempted.current = true;
      handleOAuthCallback();
    }
  }, [signedIn, isOAuthLogin]);

  useEffect(() => {
    if (signedIn && isDeviceLogin && prefillCode && !deviceAutoSubmitted.current && !deviceSuccess) {
      deviceAutoSubmitted.current = true;
      handleDeviceCodeSubmit();
    }
  }, [signedIn, isDeviceLogin, prefillCode, deviceSuccess]);

  useEffect(() => {
    if (!user || isOAuthLogin || isDeviceLogin) return;
    redirectAfterLogin({ safeNext, reload, navigate });
  }, [user, isOAuthLogin, isDeviceLogin, safeNext, reload, navigate]);

  const handleOAuthCallback = async () => {
    if (!isOAuthLogin || !redirectUri || !codeChallenge || !oauthState) return;

    const firebaseUser = auth?.currentUser;
    if (!firebaseUser) {
      setError("Authentication failed - no user");
      return;
    }

    try {
      const idToken = await firebaseUser.getIdToken();
      const refreshToken = firebaseUser.refreshToken;

      const response = await fetch("/api/oauth/complete", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          redirect_uri: redirectUri,
          code_challenge: codeChallenge,
          code_challenge_method: codeChallengeMethod || "S256",
          state: oauthState,
          firebase_id_token: idToken,
          firebase_refresh_token: refreshToken || "",
          email: firebaseUser.email || "",
          uid: firebaseUser.uid,
        }),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.detail || `OAuth failed (${response.status})`);
      }

      const data = await response.json();
      window.location.href = data.redirect_url;
      setCliSuccess(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to complete OAuth");
    }
  };

  const handleDeviceCodeSubmit = async () => {
    if (!deviceCode.trim()) return;
    setDeviceSubmitting(true);
    setError("");

    const firebaseUser = auth?.currentUser;
    if (!firebaseUser) {
      setError("Please sign in first, then enter your code.");
      setDeviceSubmitting(false);
      return;
    }

    try {
      const idToken = await firebaseUser.getIdToken();
      const refreshToken = firebaseUser.refreshToken;

      const resp = await fetch("/api/oauth/device/complete", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_code: deviceCode.trim().toUpperCase(),
          firebase_id_token: idToken,
          firebase_refresh_token: refreshToken || "",
        }),
      });

      if (!resp.ok) {
        const body = await resp.json().catch(() => ({ detail: "Unknown error" }));
        throw new Error(body.detail || `Error ${resp.status}`);
      }

      setDeviceSuccess(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to verify code");
    } finally {
      setDeviceSubmitting(false);
    }
  };

  const afterSignIn = async () => {
    if (isOAuthLogin) {
      await handleOAuthCallback();
    }
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!auth) return;
    setSubmitting(true);
    setError("");
    try {
      if (authMode === "signup") {
        await createUserWithEmailAndPassword(auth, email, password);
        trackEvent("sign_up", { method: "email" });
        void claimEvidenceSignup();
      } else {
        await signInWithEmailAndPassword(auth, email, password);
        trackEvent("login", { method: "email" });
      }
      await afterSignIn();
    } catch (err) {
      setError(
        formatFirebaseAuthError(err, authMode === "signup" ? "Could not create account" : "Sign-in failed")
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleSignIn = async () => {
    if (!auth) return;
    setError("");
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      // Google creates the account on first sign-in whichever mode the form was in.
      const isNewUser = getAdditionalUserInfo(result)?.isNewUser ?? false;
      trackEvent(isNewUser ? "sign_up" : "login", { method: "google" });
      if (isNewUser) void claimEvidenceSignup();
      await afterSignIn();
    } catch (err) {
      setError(formatFirebaseAuthError(err, "Google sign-in failed"));
    }
  };

  if (deviceSuccess || cliSuccess) {
    return (
      <div className="login-editorial flex min-h-screen flex-col items-center justify-center bg-white px-4">
        <SkipToMainLink />
        <LogoLockup className="mb-8" compactPaperclip={false} />
        <main id="main-content" className="w-full max-w-md border border-zinc-200 bg-white p-8 sm:p-10">
          <div className="text-center space-y-4">
            <CheckCircle className="mx-auto h-10 w-10 text-zinc-900" strokeWidth={1.25} />
            <h1 className="font-display text-[2rem] leading-none tracking-tight text-zinc-900">Authenticated</h1>
            <p className="text-base text-zinc-500">
              You can close this tab and return to your terminal.
            </p>
          </div>
        </main>
      </div>
    );
  }

  if (isDeviceLogin && signedIn) {
    return (
      <div className="login-editorial flex min-h-screen flex-col items-center justify-center bg-white px-4">
        <SkipToMainLink />
        <LogoLockup className="mb-8" compactPaperclip={false} />
        <main id="main-content" className="w-full max-w-md border border-zinc-200 bg-white p-8 sm:p-10">
          <h2 className="font-display mb-2 text-[2rem] leading-none tracking-tight text-zinc-900">
            Enter your code
          </h2>
          <p className="mb-7 text-base leading-snug text-zinc-500">
            Enter the code shown in your terminal to connect Paperclip.
          </p>
          {error && (
            <div
              id="device-code-error"
              role="alert"
              className="login-error"
            >
              <AlertCircle className="h-4 w-4 shrink-0" aria-hidden />
              {error}
            </div>
          )}
          <label htmlFor="device-code" className="sr-only">
            Device code
          </label>
          <Input
            id="device-code"
            value={deviceCode}
            onChange={(e) => setDeviceCode(e.target.value.toUpperCase())}
            placeholder="XXXX-XXXX"
            error={!!error}
            aria-describedby={error ? "device-code-error" : undefined}
            className="mb-4 h-auto w-full px-4 py-3 text-center font-mono text-2xl tracking-widest"
            maxLength={9}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter") handleDeviceCodeSubmit();
            }}
          />
          <Button
            className="login-primary w-full"
            onClick={handleDeviceCodeSubmit}
            loading={deviceSubmitting}
            disabled={deviceCode.trim().length < 9}
          >
            {deviceSubmitting ? "Verifying..." : "Submit"}
          </Button>
        </main>
        <LoginLegalLinks />
      </div>
    );
  }

  const isCliContext = isOAuthLogin || isDeviceLogin;
  const title =
    authMode === "forgot"
      ? "Reset your password"
      : authMode === "signup"
        ? "Create account"
        : isCliContext
          ? "Sign in to Paperclip"
          : "Welcome back";
  const description =
    authMode === "forgot"
      ? "Enter your email and we'll send you a reset link"
      : isCliContext
        ? "Then return to your terminal."
        : null;

  return (
    <div className="login-editorial flex min-h-screen flex-col items-center justify-center bg-white px-4">
      <SkipToMainLink />
      <LogoLockup className="mb-8" compactPaperclip={false} />
      <main id="main-content" className="w-full max-w-md border border-zinc-200 bg-white p-8 sm:p-10">
        <h2 className={`font-display text-[2rem] leading-none tracking-tight text-zinc-900 ${description ? "mb-2" : "mb-7"}`}>{title}</h2>
        {description && <p className="mb-7 text-base leading-snug text-zinc-500">{description}</p>}

        {error && (
          <div
            id="login-error"
            role="alert"
            className="login-error"
          >
            <AlertCircle className="h-4 w-4 shrink-0" aria-hidden />
            {error}
          </div>
        )}

        {authMode === "forgot" ? (
          <ForgotPasswordForm
            onBackToLogin={() => {
              setAuthMode("signin");
              setError("");
            }}
          />
        ) : (
          <>
            <form onSubmit={handleEmailSubmit} className="space-y-4 mb-4" aria-describedby={error ? "login-error" : undefined}>
              <div>
                <label htmlFor="email" className="login-label">
                  Email
                </label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                />
              </div>
              <div>
                <label htmlFor="password" className="login-label">
                  Password
                </label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={authMode === "signup" ? "Choose a password" : "Enter your password"}
                  autoComplete={authMode === "signup" ? "new-password" : "current-password"}
                />
              </div>
              {authMode === "signin" && (
                <div className="text-right">
                  <Button
                    variant="ghost"
                    className="login-ghost text-zinc-500"
                    onClick={() => {
                      setAuthMode("forgot");
                      setError("");
                    }}
                  >
                    Forgot password?
                  </Button>
                </div>
              )}
              <Button
                type="submit"
                className="login-primary w-full"
                loading={submitting}
              >
                {submitting
                  ? authMode === "signup"
                    ? "Creating account..."
                    : "Signing in..."
                  : authMode === "signup"
                    ? "Create account"
                    : "Sign in"}
              </Button>
            </form>

            <div className="mt-5 text-sm">
              {authMode === "signin" ? (
                <>
                  <span className="text-zinc-500">Don&apos;t have an account? </span>
                  <Button
                    variant="ghost"
                    className="login-ghost"
                    onClick={() => {
                      setAuthMode("signup");
                      setError("");
                    }}
                  >
                    Create account
                  </Button>
                </>
              ) : (
                <>
                  <span className="text-zinc-500">Already have an account? </span>
                  <Button
                    variant="ghost"
                    className="login-ghost"
                    onClick={() => {
                      setAuthMode("signin");
                      setError("");
                    }}
                  >
                    Sign in
                  </Button>
                </>
              )}
            </div>

            <div className="my-5 flex items-center gap-3">
              <div className="flex-1 border-t border-zinc-200" />
              <span className="font-mono text-3xs uppercase tracking-[0.14em] text-zinc-400">or</span>
              <div className="flex-1 border-t border-zinc-200" />
            </div>

            <Button
              variant="outline"
              onClick={handleGoogleSignIn}
              className="login-outline w-full"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
              </svg>
              Continue with Google
            </Button>
          </>
        )}
      </main>
      <LoginLegalLinks />
    </div>
  );
};
